import 'rxjs/add/observable/throw';
import 'rxjs/add/operator/catch';
import 'rxjs/add/operator/map';
import { stringify } from 'query-string';
import { Observable } from 'rxjs/Observable';
import fetchJson from '@atlassian/jira-fetch/src/utils/as-json.tsx';
import { fetchJson$ } from '@atlassian/jira-servicedesk-common/src/utils/fetch/index.tsx';
import type { AccountId } from '@atlassian/jira-shared-types/src/general.tsx';
import {
	type Article,
	type Categories,
	type CategoryGuid,
	type KbLink,
	categoryGuidToString,
	type AppLinkId,
	type UpdateLabelRequest,
	type Label,
	type LabelSuggestions,
	type LabelSuggestionsResponse,
} from '../../../types.tsx';
import { APOLLO_CONTEXT_ROOT, APOLLO_STARGATE_CONTEXT_ROOT } from '../constants.tsx';

export type ArticleData = Omit<Article, 'categories' | 'isBusy' | 'labelState'> & {
	categories: Categories | undefined;
	categoriesSupported?: boolean;
	labels?: Label[];
};

/**
 * This will replace `null` with `undefined`. We want to merge
 * data later in the application - but we don't want null to
 * override data that already exists.
 */
const replaceArticleNullsWithUndefined = (article: ArticleData) => {
	const parsedArticle = { ...article };

	if (parsedArticle.viewCount === null) {
		parsedArticle.viewCount = undefined;
	}

	if (parsedArticle.categories === null) {
		parsedArticle.categories = undefined;
	}

	if (parsedArticle.featuredInCategories === null) {
		parsedArticle.featuredInCategories = undefined;
	}

	return parsedArticle;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const transformArticleResponse = (articleResponse: any): ArticleData => {
	// Currently API return ADF as string instead of a JSON blob, so we have to
	// an additional transformation step here.
	let bodyAdf;
	if (articleResponse.body) {
		bodyAdf = JSON.parse(articleResponse.body);
	}

	const transformedData: ArticleData = {
		...articleResponse,
		body: bodyAdf,
	};
	return replaceArticleNullsWithUndefined(transformedData);
};

export type Expand = 'viewCount' | 'category' | 'body' | 'createdBy' | 'updatedBy' | 'labels';
export type OrderByField = 'featured' | 'lastModified' | 'title';
export type OrderBy =
	| '-featured'
	| '+featured'
	| '-lastModified'
	| '+lastModified'
	| '-title'
	| '+title';

export type ListArticlesResponse = {
	results: ArticleData[];
	categoriesSupported: boolean;
	totalSize: number;
	start: number;
	limit: number;
	size: number;
};

export type ListOptions = {
	query?: string;
	start?: number;
	limit?: number;
	excludeCategoryId?: CategoryGuid;
	expand?: Expand[];
	orderby?: OrderBy;
	spaceKey?: string[];
	cursor?: string;
};

export type SpacesResponse = {
	spaces: {
		name: string;
		key: string;
		appLinkId: AppLinkId;
	}[];
};

const getQueryString = (opts: ListOptions) => {
	const { expand, ...otherOpts } = opts;
	if (expand) {
		return stringify({ ...otherOpts, expand: expand.join(',') });
	}

	return stringify(otherOpts);
};

export const list = (
	projectId: number,
	opts: ListOptions,
	activationId: string,
	apolloPath: string = APOLLO_STARGATE_CONTEXT_ROOT,
): Observable<ListArticlesResponse> => {
	const queryString: string = getQueryString(opts);
	const apiPath = `/api/project/${projectId}/article?${queryString}`;
	const url = `${apolloPath}/${activationId}${apiPath}`;

	return fetchJson$(url).map((response) => ({
		...response,
		results: response.results.map(transformArticleResponse),
	}));
};

export const listCategoryArticles = (
	projectId: number,
	categoryId: CategoryGuid,
	opts: ListOptions,
	activationId: string,
	apolloPath: string = APOLLO_STARGATE_CONTEXT_ROOT,
): Observable<ListArticlesResponse> => {
	const queryString = getQueryString(opts);
	const apiPath = `/api/project/${projectId}/category/${categoryGuidToString(
		categoryId,
	)}/article?${queryString}`;
	const url = `${apolloPath}/${activationId}${apiPath}`;

	return fetchJson$(url).map((response) => ({
		...response,
		results: response.results.map(transformArticleResponse),
	}));
};

export const getArticle = (
	projectId: number,
	articleId: number | string,
	withCategory: boolean,
	apolloPath: string = APOLLO_CONTEXT_ROOT,
): Observable<ArticleData> => {
	const url = `${apolloPath}/api/project/${projectId}/article/${articleId}`;
	const queryString = withCategory ? `?${stringify({ expand: 'category' })}` : '';
	return fetchJson$(`${url}${queryString}`).map(transformArticleResponse);
};

export const getArticleWithExpandParams = (
	projectId: number,
	articleId: number | string,
	activationId: string,
	expandParams: Expand[],
	apolloPath: string = APOLLO_STARGATE_CONTEXT_ROOT,
): Observable<ArticleData> => {
	const url = `${apolloPath}/${activationId}/api/project/${projectId}/article/${articleId}`;
	const queryString = expandParams.length ? `?${getQueryString({ expand: expandParams })}` : '';
	return fetchJson$(`${url}${queryString}`).map(transformArticleResponse);
};

export type UpdateCategoriesForArticleRequest = {
	projectId: number;
	categoryIds: CategoryGuid[];
	articleId: string;
	spaceKey: string;
	activationId?: string;
	apolloPath?: string;
};

export const addCategoriesForArticle = ({
	projectId,
	categoryIds,
	articleId,
	spaceKey,
	activationId,
	apolloPath = APOLLO_STARGATE_CONTEXT_ROOT,
}: UpdateCategoriesForArticleRequest): Observable<void> => {
	const url = `${apolloPath}/${activationId}/api/project/${projectId}/space/${spaceKey}/article/${articleId}/category`;
	return fetchJson$(url, {
		method: 'PATCH',
		body: JSON.stringify({
			categories: categoryIds,
		}),
	}).catch(({ response }) => Observable.throw(response));
};

export const updateCategoriesForArticle = ({
	projectId,
	categoryIds,
	articleId,
	spaceKey,
	activationId,
	apolloPath = APOLLO_STARGATE_CONTEXT_ROOT,
}: UpdateCategoriesForArticleRequest): Observable<void> => {
	const url = `${apolloPath}/${activationId}/api/project/${projectId}/article/${articleId}/category`;

	return fetchJson$(url, {
		method: 'PUT',
		body: JSON.stringify({
			spaceKey,
			categories: categoryIds,
		}),
	}).catch(({ response }) => Observable.throw(response));
};

export const getAllLinkedSpaces = (projectKey: string): Observable<SpacesResponse> => {
	const url = `/rest/servicedesk/1/servicedesk/${projectKey}/kb`;
	const transformedResponse = fetchJson$(url).map((response) => ({
		spaces: response.map((space: KbLink) => ({
			name: space.spaceName,
			key: space.spaceKey,
			appLinkId: space.appLinkId,
		})),
	}));
	return transformedResponse;
};

export type AddLabelsRequest = {
	projectId: number;
	labels: UpdateLabelRequest[];
	articleId: string;
	spaceKey: string;
	activationId?: string;
	apolloPath?: string;
};

export type AddLabelsResponse = {
	labels: Label[];
	articleId: string;
};

export const addLabelsToArticles = ({
	projectId,
	labels,
	articleId,
	spaceKey,
	activationId,
	apolloPath = APOLLO_STARGATE_CONTEXT_ROOT,
}: AddLabelsRequest): Observable<AddLabelsResponse> => {
	const url = `${apolloPath}/${activationId}/api/project/${projectId}/space/${spaceKey}/article/${articleId}/label`;
	return fetchJson$(url, {
		method: 'POST',
		body: JSON.stringify(labels),
	}).catch(({ response }) => Observable.throw(response));
};

export type RemoveLabelRequest = {
	projectId: number;
	label: string;
	articleId: string;
	spaceKey: string;
	activationId: string;
	apolloPath?: string;
};

export const removeLabelsFromArticles = ({
	projectId,
	label,
	articleId,
	spaceKey,
	activationId,
	apolloPath = APOLLO_STARGATE_CONTEXT_ROOT,
}: RemoveLabelRequest): Observable<void> => {
	const url = `${apolloPath}/${activationId}/api/project/${projectId}/space/${spaceKey}/article/${articleId}/label/${encodeURIComponent(
		label,
	)}`;
	return fetchJson$(url, {
		method: 'DELETE',
	}).catch(({ response }) => Observable.throw(response));
};

export const fetchLabelSuggestions = (
	projectKey: string,
	queryString: string,
	articleId: string,
): Promise<LabelSuggestions[]> => {
	const url = `/rest/servicedesk/1/servicedesk/${projectKey}/kb/labels?query=${queryString}&contentId=${articleId}`;
	return fetchJson(url)
		.then(({ contentNameMatches }: LabelSuggestionsResponse) => {
			if (!contentNameMatches?.length) {
				return [];
			}
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			const suggestions = contentNameMatches.reduce<Array<any>>((labelList, contentNameMatch) => {
				if (contentNameMatch.length) {
					// eslint-disable-next-line jira/js/no-reduce-accumulator-spread
					return [...labelList, ...contentNameMatch];
				}
				return labelList;
			}, []);
			return suggestions.length
				? suggestions.map(({ name }) => ({
						label: name,
						value: name,
					}))
				: [];
		})
		.catch(() => []);
};

export const fetchArticleEditPermission = (
	cloudId: string,
	articleId: string,
	projectId: number,
	accountId: AccountId | null,
	sendCustomHeaders: boolean,
	spaceKey: string,
): Observable<{
	hasPermission: boolean;
}> => {
	const body = {
		subject: {
			type: 'user',
			identifier: accountId,
		},
		operation: 'update',
	};
	const headers = sendCustomHeaders
		? {
				'Content-Type': 'application/json',
				'X-ATL-EmbeddablePage-EntityIds': JSON.stringify({
					spaceKey: spaceKey || '',
					parentProduct: 'JSM',
					parentProductContentContainerId: projectId,
					contentId: articleId,
				}),
			}
		: { 'Content-Type': 'application/json' };

	return fetchJson$(
		`/gateway/api/ex/confluence/${cloudId}/wiki/rest/api/content/${articleId}/permission/check`,
		{
			method: 'POST',
			// @ts-expect-error - TS-2322 Please check what's correct: either add `headers` to fetchJson$ `options` type, or remove this argument, if function does not support it and it does nothing
			headers,
			body: JSON.stringify(body),
		},
	).catch((error) => Observable.throw(error));
};
